import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { WipAgingItem, ApiResponse, ESTADO_LABELS } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const COLUMNA_ORDER: Array<WipAgingItem['columna']> = [
    'en_progreso', 'esperando_cliente', 'testing', 'por_hacer', 'backlog'
];

const ageColor = (dias: number, bloqueado: boolean): string => {
    if (bloqueado) return 'bg-red-50 border-red-300';
    if (dias > 7) return 'bg-red-50 border-red-200';
    if (dias > 3) return 'bg-amber-50 border-amber-200';
    return 'bg-white border-slate-200';
};

const ageBadgeColor = (dias: number, bloqueado: boolean): string => {
    if (bloqueado) return 'bg-red-200 text-red-800';
    if (dias > 7) return 'bg-red-100 text-red-700';
    if (dias > 3) return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-600';
};

const PRIORIDAD_ICON: Record<string, string> = { alta: '🔴', media: '🟡', baja: '🟢' };

export const CuellosBotellaPanel: React.FC = () => {
    const [items, setItems] = useState<WipAgingItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filtroColumna, setFiltroColumna] = useState<string>('todas');

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiFetch(`${API_BASE}/tareas/wip-aging`);
            const json: ApiResponse<WipAgingItem[]> = await res.json();
            if (json.success && json.data) setItems(json.data);
            else setError(json.error || 'Error al cargar WIP aging');
        } catch (e) {
            setError('No se pudo conectar con el servidor');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const itemsFiltrados = filtroColumna === 'todas'
        ? items
        : items.filter(i => i.columna === filtroColumna);

    const itemsOrdenados = [...itemsFiltrados].sort((a, b) => {
        if (a.bloqueado !== b.bloqueado) return a.bloqueado ? -1 : 1;
        return b.dias_en_columna - a.dias_en_columna;
    });

    const bloqueadosCount = items.filter(i => i.bloqueado).length;
    const estancadosCount = items.filter(i => i.dias_en_columna > 7).length;

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-base font-semibold text-slate-800">🔍 Cuellos de Botella</h3>
                    <p className="text-xs text-slate-500">
                        Items ordenados por días en columna actual. Bloqueados primero.
                    </p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                    {bloqueadosCount > 0 && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                            🚫 {bloqueadosCount} bloqueado{bloqueadosCount > 1 ? 's' : ''}
                        </span>
                    )}
                    {estancadosCount > 0 && (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">
                            ⏰ {estancadosCount} &gt; 7 días
                        </span>
                    )}
                    <button
                        onClick={fetchData}
                        className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded text-xs"
                        disabled={loading}
                    >
                        {loading ? '⟳' : '↻'} Refrescar
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-1 mb-3">
                <button
                    onClick={() => setFiltroColumna('todas')}
                    className={`px-3 py-1 text-xs rounded transition-colors ${
                        filtroColumna === 'todas'
                            ? 'bg-slate-800 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                >
                    Todas ({items.length})
                </button>
                {COLUMNA_ORDER.map(col => {
                    const count = items.filter(i => i.columna === col).length;
                    if (count === 0) return null;
                    return (
                        <button
                            key={col}
                            onClick={() => setFiltroColumna(col)}
                            className={`px-3 py-1 text-xs rounded transition-colors ${
                                filtroColumna === col
                                    ? 'bg-slate-800 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            {ESTADO_LABELS[col]} ({count})
                        </button>
                    );
                })}
            </div>

            {error && <div className="text-red-500 text-sm py-4">{error}</div>}

            {!error && itemsOrdenados.length === 0 && !loading && (
                <div className="py-8 text-center text-slate-400 text-sm">
                    ✅ Sin items para mostrar. El sistema fluye bien.
                </div>
            )}

            <div className="space-y-2 max-h-[500px] overflow-y-auto scrollbar-thin">
                {itemsOrdenados.map(item => (
                    <div
                        key={item.id}
                        className={`flex items-start gap-3 p-3 border rounded-lg ${ageColor(item.dias_en_columna, item.bloqueado)}`}
                    >
                        <div
                            className="w-1 self-stretch rounded-full flex-shrink-0"
                            style={{ backgroundColor: item.cliente_color }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-mono text-slate-500">#{item.id}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${ageBadgeColor(item.dias_en_columna, item.bloqueado)}`}>
                                    {item.bloqueado ? '🚫 BLOQUEADO' : `${item.dias_en_columna}d`}
                                </span>
                                <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded uppercase">
                                    {ESTADO_LABELS[item.columna]}
                                </span>
                                {item.clase_servicio === 'expedito' && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">⚡ EXPEDITO</span>
                                )}
                                {item.clase_servicio === 'fecha_fija' && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">📌 FECHA FIJA</span>
                                )}
                            </div>
                            <div className="text-sm font-medium text-slate-800 mt-1 truncate">{item.titulo}</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                                {PRIORIDAD_ICON[item.prioridad]} {item.prioridad} · {item.empresa}
                                {item.responsable && ` · 👤 ${item.responsable}`}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
